import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('tdo_token'));

  const axiosAuth = axios.create({ baseURL: API, withCredentials: true });
  if (token) axiosAuth.defaults.headers.common['Authorization'] = `Bearer ${token}`;

  useEffect(() => {
    const checkAuth = async () => {
      // CRITICAL: If returning from OAuth callback (#session_id in URL), defer to AuthCallback.
      // It will exchange the session_id and persist the JWT, then navigate. We must not
      // race-call /auth/me before the token is stored.
      if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
        setLoading(false);
        return;
      }
      const stored = localStorage.getItem('tdo_token');
      if (!stored) { setLoading(false); return; }
      try {
        const resp = await axios.get(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${stored}` }, withCredentials: true, timeout: 8000,
        });
        setUser(resp.data);
      } catch {
        localStorage.removeItem('tdo_token');
        setToken(null);
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, telefone, senha) => {
    const body = { senha };
    if (email) body.email = email;
    if (telefone) body.telefone = telefone;
    const resp = await axios.post(`${API}/api/auth/login`, body, { withCredentials: true, timeout: 20000 });
    const data = resp.data;
    if (data.token) { localStorage.setItem('tdo_token', data.token); setToken(data.token); }
    setUser(data);
    return data;
  };

  const register = async (nome, email, telefone, senha, emailRecuperacao = null) => {
    const payload = { nome, email, telefone, senha };
    if (emailRecuperacao) payload.email_recuperacao = emailRecuperacao;
    // Referral code em localStorage (setado no Landing quando ?ref= aparece na URL)
    const ref = localStorage.getItem('tdo_ref');
    if (ref) payload.referral_code = ref;
    const resp = await axios.post(`${API}/api/auth/register`, payload, { withCredentials: true, timeout: 20000 });
    const data = resp.data;
    if (data.token) { localStorage.setItem('tdo_token', data.token); setToken(data.token); }
    if (ref) localStorage.removeItem('tdo_ref');  // consome só uma vez
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true }); } catch {}
    localStorage.removeItem('tdo_token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    const stored = localStorage.getItem('tdo_token');
    if (!stored) return null;
    try {
      const resp = await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${stored}` }, withCredentials: true, timeout: 8000,
      });
      setUser(resp.data);
      return resp.data;
    } catch { return null; }
  };

  const isPro = user?.plano === 'pro';
  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, isPro, isAdmin, token }}>
      {children}
    </AuthContext.Provider>
  );
};
